<template>
  <div v-if="isVisible" class="category-nav-node">
    <div
      class="category-nav-item"
      :class="{
        'active': selectedCategoryKod === category.kod && !hasVisibleChildren,
        'has-children': hasVisibleChildren,
        'is-parent': hasVisibleChildren,
        'search-match': isDirectMatch
      }"
      @click="handleClick"
    >
      <div class="category-nav-content">
        <span
          v-if="hasVisibleChildren"
          class="category-nav-toggle"
        >
          <b-icon-chevron-right v-if="collapsedCategories[category.kod]"></b-icon-chevron-right>
          <b-icon-chevron-down v-else></b-icon-chevron-down>
        </span>
        <span class="category-nav-name">{{ category.nev }}</span>
      </div>
      <b-icon-chevron-right v-if="!hasVisibleChildren" class="category-nav-arrow"></b-icon-chevron-right>
    </div>

    <div v-if="hasVisibleChildren && !collapsedCategories[category.kod]">
      <category-tree-nav-node
        v-for="child in visibleChildren"
        :key="child.kod"
        :category="child"
        :collapsed-categories="collapsedCategories"
        :selected-category-kod="selectedCategoryKod"
        :search-filter="searchFilter"
        :level="level + 1"
        @toggle="$emit('toggle', $event)"
        @select="$emit('select', $event)"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'CategoryTreeNavNode',
  props: {
    category: {
      type: Object,
      required: true
    },
    collapsedCategories: {
      type: Object,
      required: true
    },
    selectedCategoryKod: {
      type: String,
      default: null
    },
    level: {
      type: Number,
      default: 0
    },
    searchFilter: {
      type: Function,
      default: null
    }
  },
  computed: {
    hasChildren() {
      return this.category.children && this.category.children.length > 0;
    },
    visibleChildren() {
      if (!this.hasChildren) {
        return [];
      }
      if (!this.searchFilter) {
        return this.category.children;
      }
      return this.category.children.filter(child => this.searchFilter(child));
    },
    hasVisibleChildren() {
      return this.visibleChildren.length > 0;
    },
    isVisible() {
      if (!this.searchFilter) {
        return true;
      }
      return this.searchFilter(this.category);
    },
    isDirectMatch() {
      if (!this.searchFilter) {
        return false;
      }
      // Check if this category's name matches directly (not just through children)
      const query = this.searchFilter.searchQuery;
      if (!query) {
        return false;
      }
      const categoryName = (this.category.nev || '').toLowerCase();
      return categoryName.includes(query.toLowerCase());
    }
  },
  methods: {
    handleClick() {
      if (this.hasVisibleChildren) {
        // If it has children, toggle expand/collapse
        this.$emit('toggle', this.category.kod);
      } else {
        // If it's a leaf node, select it
        this.$emit('select', this.category.kod);
      }
    }
  }
}
</script>

<style scoped>
.category-nav-node {
  display: flex;
  flex-direction: column;
}

.category-nav-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 0.75rem;
  background: white;
  border: 1px solid #e7e5e4;
  border-radius: 0.5rem;
  margin-bottom: 0.25rem;
  transition: all 0.2s ease;
  position: relative;
}

.category-nav-item:hover {
  background: #f8fafc;
  border-color: #0f766e;
}

.category-nav-item.active {
  background: #f0fdfa;
  border-color: #0f766e;
  border-left-width: 4px;
}

.category-nav-item.is-parent {
  cursor: pointer;
  background: #fafafa;
  font-weight: 600;
}

.category-nav-item.is-parent:hover {
  background: #f1f5f9;
}

/* Visual indicator for nested items */
.category-nav-node .category-nav-node {
  position: relative;
  padding-left: 0.5rem;
  margin-left: 0.75rem;
  margin-top: 0.25rem;
}

.category-nav-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.category-nav-toggle {
  color: #0f766e;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  padding: 0.125rem;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.category-nav-name {
  font-size: 0.9375rem;
  font-weight: 500;
  color: #0f172a;
  flex: 1;
}

.category-nav-item.active .category-nav-name {
  color: #0f766e;
  font-weight: 600;
}

.category-nav-arrow {
  color: #cbd5e1;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.category-nav-item:hover .category-nav-arrow,
.category-nav-item.active .category-nav-arrow {
  color: #0f766e;
}

.category-nav-item.search-match {
  background: #fef9c3;
  border-color: #eab308;
}

.category-nav-item.search-match:hover {
  background: #fef08a;
  border-color: #ca8a04;
}
</style>
